import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Check, 
  Plus,
  Minus,
  Users, 
  CreditCard, 
  BarChart3, 
  Shield, 
  Zap, 
  Globe,
  Menu,
  X,
  Clock,
  FileText,
  Palette,
  Mail,
  BookOpen,
  Layers,
  PieChart,
  Bell,
  Settings,
  ShoppingCart,
  MessageSquare,
  TrendingUp,
  Calendar,
  Briefcase,
  Lightbulb,
  Target,
  Headphones
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import GradientText from '../../components/reactbits/GradientText';
import StarBorder from '../../components/reactbits/StarBorder';
import TetrahedronMonolith from '../../components/TetrahedronMonolith';
import DotGrid from '../../components/reactbits/DotGrid';
import PublicAIInput from '../../components/chat/PublicAIInput';
import MorphingNavbar from '../../components/MorphingNavbar';
import { RevealSection } from '../../components/ScrollReveal';

// ============================================
// EDITABLE CONTENT - Change these values
// ============================================

const SITE_CONFIG = {
  name: 'Edrano',
  tagline: 'The foundation your agency runs on',
  heroHeadline: 'Build on a solid foundation.',
  heroSubheadline: 'Scale your digital services with Edrano\'s modular payment and subscription tools—designed to support your growth from the ground up.',
  heroCTA1: 'Get started',
  heroCTA2: 'See Edrano in action',
  heroGradient: 'linear-gradient(135deg, #E8E4DF 0%, #D4CFC7 100%)',
};

// Use Case Tabs - Problems and Solutions
const USE_CASE_TABS = [
  {
    id: 'agency',
    label: 'AGENCY',
    problem: 'Managing 20+ clients across spreadsheets?',
    solution: 'One dashboard for all clients, services, and revenue.',
    icon: Briefcase
  },
  {
    id: 'studio',
    label: 'STUDIO',
    problem: 'Losing track of project deliverables?',
    solution: 'Automated service tracking and renewal reminders.',
    icon: Palette
  },
  {
    id: 'digital',
    label: 'DIGITAL',
    problem: 'Chasing invoices and payments?',
    solution: 'Stripe-powered billing with auto-reminders.',
    icon: Zap
  },
  {
    id: 'creative',
    label: 'CREATIVE',
    problem: 'Looking unprofessional to clients?',
    solution: 'White-label portal with your brand and domain.',
    icon: Lightbulb
  }
];

const FEATURES = [
  {
    label: 'SEAMLESS CLIENT MANAGEMENT',
    headline: 'Work from anywhere, stay in sync',
    description: 'Manage all your clients, services, and communications from one beautiful dashboard. Access everything you need, wherever you are.',
    gradient: 'linear-gradient(135deg, #E0DDD8 0%, #C9C4BC 100%)',
    layout: 'image-left',
    mockupType: 'clients' // Dashboard showing client list
  },
  {
    label: 'SERVICE MANAGEMENT',
    headline: 'Keep every service moving forward',
    description: 'Create service packages, track deliverables, and automate renewals. Your clients always know what they\'re getting.',
    gradient: 'linear-gradient(135deg, #E5E8EC 0%, #C8D0D8 100%)',
    subFeatures: ['Service Catalog', 'Auto-Renewals', 'Client Portal', 'Knowledge Base'],
    layout: 'image-right',
    mockupType: 'services' // Services management view
  },
  {
    label: 'FINANCIAL MANAGEMENT',
    headline: 'Track income, get paid, stress less',
    description: 'Accept payments via Stripe, track revenue trends, and never miss a renewal. Beautiful reports show your business health at a glance.',
    gradient: 'linear-gradient(135deg, #E8EBE5 0%, #D0D8C8 100%)',
    subFeatures: ['Stripe Payments', 'Revenue Analytics', 'Auto-Invoicing', 'Tax Reports'],
    layout: 'image-left',
    mockupType: 'analytics' // Analytics dashboard
  }
];

const FAQ_ITEMS = [
  {
    question: 'What is Edrano?',
    answer: 'Edrano is a white-label client portal platform that helps agencies manage clients, sell services, and accept payments — all under your own brand.'
  },
  {
    question: 'What makes Edrano different?',
    answer: 'Unlike generic project management tools, Edrano is purpose-built for selling services. You get client portals, Stripe payments, knowledge base, and white-label branding in one affordable platform — starting at just $39/month vs $99+ for competitors.'
  },
  {
    question: 'Does Edrano use AI?',
    answer: 'We\'re building AI features including smart support responses, service descriptions generator, and revenue insights. These are coming in Q2 2026.'
  },
  {
    question: 'Can I invite my whole team?',
    answer: 'Yes! Depending on your plan, you can add 5-20 team members with role-based permissions. Scale plan includes 20 team members.'
  },
  {
    question: 'Is my data secure?',
    answer: 'Absolutely. We use enterprise-grade security with encrypted data, isolated databases per tenant, and are SOC 2 ready. Your data is never shared between accounts.'
  },
  {
    question: 'How does Edrano compare to SPP.co or SuiteDash?',
    answer: 'Edrano offers similar features at a fraction of the cost. SPP starts at $99/mo, SuiteDash can feel overwhelming. Edrano starts at $39/mo with a cleaner, more focused interface. See our detailed comparison page for more.'
  }
];

const PRICING_TIERS = [
  {
    name: 'Starter',
    description: 'Perfect for freelancers and solo agencies',
    monthlyPrice: 39,
    annualPrice: 390,
    features: [
      '100 clients',
      '50 services',
      '5 team members',
      'Subdomain portal',
      'Knowledge base',
      'Email support'
    ],
    cta: 'Start free trial',
    popular: false
  },
  {
    name: 'Growth',
    description: 'For growing agencies with multiple clients',
    monthlyPrice: 79,
    annualPrice: 790,
    features: [
      '300 clients',
      '150 services',
      '10 team members',
      '1 custom domain',
      '5 webhooks',
      'API access (read)',
      'Priority support'
    ],
    cta: 'Start free trial',
    popular: true
  },
  {
    name: 'Scale',
    description: 'For established agencies with high volume',
    monthlyPrice: 149,
    annualPrice: 1490,
    features: [
      '500 clients',
      '300 services',
      '20 team members',
      '3 custom domains',
      '25 webhooks',
      'Full API access',
      'Remove branding',
      'Phone support'
    ],
    cta: 'Start free trial',
    popular: false
  }
];

const FOOTER_LINKS = {
  main: [
    { name: 'Features', href: '/features' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Use Cases', href: '/use-cases' },
    { name: 'Compare', href: '/compare' }
  ],
  more: [
    { name: 'Docs', href: '/docs' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'GDPR', href: '/gdpr' }
  ]
};

// ============================================
// DASHBOARD MOCKUP COMPONENTS
// ============================================

function ClientsDashboardMockup() {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-stone-100 px-4 py-2 flex items-center gap-2 border-b border-stone-200">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
        </div>
        <div className="flex-1 text-center">
          <span className="text-xs text-stone-400">app.youragency.com/clients</span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-stone-800 text-sm">All Clients</h4>
          <div className="px-2 py-1 bg-stone-900 text-white text-xs rounded">+ Add Client</div>
        </div>
        <div className="space-y-2">
          {[
            { name: 'Acme Corp', services: 3, revenue: '$2,450', status: 'active' },
            { name: 'TechStart Inc', services: 2, revenue: '$1,200', status: 'active' },
            { name: 'Design Studio', services: 4, revenue: '$3,100', status: 'pending' },
            { name: 'Local Bakery', services: 1, revenue: '$450', status: 'active' }
          ].map((client, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-xs font-medium text-stone-600">
                  {client.name.charAt(0)}
                </div>
                <div>
                  <p className="text-xs font-medium text-stone-800">{client.name}</p>
                  <p className="text-[10px] text-stone-400">{client.services} services</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold text-stone-800">{client.revenue}</p>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${client.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {client.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ServicesDashboardMockup() {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-stone-100 px-4 py-2 flex items-center gap-2 border-b border-stone-200">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
        </div>
        <div className="flex-1 text-center">
          <span className="text-xs text-stone-400">app.youragency.com/services</span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-stone-800 text-sm">Service Catalog</h4>
          <div className="flex gap-2">
            <span className="text-xs text-stone-400">6 active</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { name: 'Starter Hosting', price: '$19/mo', icon: '🚀' },
            { name: 'Pro Care Plan', price: '$149/mo', icon: '⭐' },
            { name: 'Speed Optimization', price: '$349', icon: '⚡' },
            { name: 'Security Audit', price: '$299', icon: '🔒' }
          ].map((service, i) => (
            <div key={i} className="p-2 bg-stone-50 rounded-lg border border-stone-100">
              <div className="text-lg mb-1">{service.icon}</div>
              <p className="text-xs font-medium text-stone-800">{service.name}</p>
              <p className="text-xs text-stone-500">{service.price}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Bell className="w-3 h-3 text-amber-600" />
            <span className="text-[10px] text-amber-700">3 renewals due this week</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsDashboardMockup() {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-stone-100 px-4 py-2 flex items-center gap-2 border-b border-stone-200">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
        </div>
        <div className="flex-1 text-center">
          <span className="text-xs text-stone-400">app.youragency.com/analytics</span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-stone-800 text-sm">Revenue Analytics</h4>
          <span className="text-xs text-green-600 font-medium">↑ 23% vs last month</span>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="p-2 bg-stone-50 rounded-lg">
            <p className="text-[10px] text-stone-400">MRR</p>
            <p className="text-lg font-bold text-stone-900">$12,450</p>
          </div>
          <div className="p-2 bg-stone-50 rounded-lg">
            <p className="text-[10px] text-stone-400">Active Clients</p>
            <p className="text-lg font-bold text-stone-900">48</p>
          </div>
        </div>
        {/* Mini chart */}
        <div className="h-16 flex items-end gap-1">
          {[35, 42, 38, 55, 48, 62, 58, 72, 68, 85, 78, 92].map((h, i) => (
            <div key={i} className="flex-1 bg-stone-200 rounded-t" style={{ height: `${h}%` }}>
              {i === 11 && <div className="w-full h-full bg-stone-800 rounded-t"></div>}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-stone-400">Jan</span>
          <span className="text-[10px] text-stone-400">Dec</span>
        </div>
      </div>
    </div>
  );
}

function DashboardMockup({ type }) {
  switch (type) {
    case 'clients':
      return <ClientsDashboardMockup />;
    case 'services':
      return <ServicesDashboardMockup />;
    case 'analytics':
      return <AnalyticsDashboardMockup />;
    default:
      return <ClientsDashboardMockup />;
  }
}

// ============================================
// FEATURE SECTION COMPONENT
// ============================================

function FeatureSection({ feature, index }) {
  const isImageLeft = feature.layout === 'image-left';
  
  return (
    <section className="py-20" data-testid={`feature-section-${index}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex flex-col ${isImageLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-20`}>
          {/* Dashboard Mockup Side */}
          <div className="flex-1 w-full">
            <div 
              className="rounded-2xl overflow-hidden p-6"
              style={{ background: feature.gradient }}
            >
              <DashboardMockup type={feature.mockupType} />
            </div>
          </div>
          
          {/* Content Side */}
          <div className="flex-1">
            <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
              {feature.label}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
              {feature.headline}
            </h2>
            <p className="mt-4 text-lg text-stone-600 leading-relaxed">
              {feature.description}
            </p>
            
            <div className="mt-6">
              <Link to="/register">
                <Button 
                  className="bg-stone-900 hover:bg-stone-800 text-white rounded-full px-6 h-11"
                  data-testid={`feature-cta-${index}`}
                >
                  Try Edrano free
                </Button>
              </Link>
            </div>
            
            {feature.subFeatures && (
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
                {feature.subFeatures.map((sub, i) => (
                  <span key={i} className="flex items-center gap-2 text-sm text-stone-600">
                    <Check className="w-4 h-4 text-stone-400" />
                    {sub}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// FAQ COMPONENT
// ============================================

function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border-b border-stone-200">
      <button
        className="w-full py-5 flex items-center justify-between text-left hover:opacity-70 transition-opacity"
        onClick={onClick}
        data-testid={`faq-${question.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`}
      >
        <span className="text-stone-800 font-medium pr-8">{question}</span>
        {isOpen ? (
          <Minus className="w-5 h-5 text-stone-400 flex-shrink-0" />
        ) : (
          <Plus className="w-5 h-5 text-stone-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-5 pr-12">
          <p className="text-stone-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function MarketingHome() {
  const [isAnnual, setIsAnnual] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [activeTab, setActiveTab] = useState('agency');

  const activeUseCase = USE_CASE_TABS.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }}>
      {/* Morphing Floating Navbar */}
      <MorphingNavbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 relative" data-testid="hero-section">
        {/* Dot Grid Background */}
        <DotGrid />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Hero Text */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              <GradientText
                colors={["#1a1a1a", "#6b6b6b", "#9ca3af", "#1a1a1a"]}
                animationSpeed={6}
                direction="horizontal"
              >
                {SITE_CONFIG.heroHeadline}
              </GradientText>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-stone-600 leading-relaxed">
              {SITE_CONFIG.heroSubheadline}
            </p>
            
            {/* CTA Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/register">
                <StarBorder
                  as="div"
                  color="#d4a574"
                  speed="4s"
                  thickness={1}
                  data-testid="hero-cta-primary"
                >
                  <span className="flex items-center gap-2">
                    {SITE_CONFIG.heroCTA1}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </StarBorder>
              </Link>
              <a href="#features">
                <StarBorder
                  as="div"
                  color="#a89070"
                  speed="5s"
                  thickness={1}
                  className="light"
                  data-testid="hero-cta-secondary"
                >
                  {SITE_CONFIG.heroCTA2}
                </StarBorder>
              </a>
              <Link to="/app/demo">
                <StarBorder
                  as="div"
                  color="#f97316"
                  speed="4s"
                  thickness={1}
                  className="light"
                  data-testid="hero-cta-demo"
                >
                  <span className="flex items-center gap-2">
                    See Live Demo
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </StarBorder>
              </Link>
            </div>
            
            {/* AI Assistant Input */}
            <div className="mt-12">
              <PublicAIInput tenantName="Edrano" />
            </div>
          </div>

          {/* Dashboard Preview */}
          <div 
            className="rounded-2xl overflow-hidden shadow-2xl"
            style={{ background: SITE_CONFIG.heroGradient }}
          >
            <div className="p-4 sm:p-8">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-stone-100 px-4 py-3 flex items-center gap-3 border-b border-stone-200">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="bg-white rounded-md px-4 py-1 text-xs text-stone-400 border border-stone-200">
                      app.youragency.com/dashboard
                    </div>
                  </div>
                </div>
                
                <div className="p-6 min-h-[400px]">
                  <div className="flex gap-6">
                    <div className="hidden sm:block w-48 space-y-2">
                      <div className="flex items-center gap-3 px-3 py-2 bg-stone-100 rounded-lg">
                        <Layers className="w-4 h-4 text-stone-600" />
                        <span className="text-sm font-medium text-stone-700">Dashboard</span>
                      </div>
                      <div className="flex items-center gap-3 px-3 py-2 text-stone-500">
                        <Users className="w-4 h-4" />
                        <span className="text-sm">Clients</span>
                      </div>
                      <div className="flex items-center gap-3 px-3 py-2 text-stone-500">
                        <FileText className="w-4 h-4" />
                        <span className="text-sm">Services</span>
                      </div>
                      <div className="flex items-center gap-3 px-3 py-2 text-stone-500">
                        <ShoppingCart className="w-4 h-4" />
                        <span className="text-sm">Orders</span>
                      </div>
                      <div className="flex items-center gap-3 px-3 py-2 text-stone-500">
                        <PieChart className="w-4 h-4" />
                        <span className="text-sm">Analytics</span>
                      </div>
                      <div className="flex items-center gap-3 px-3 py-2 text-stone-500">
                        <MessageSquare className="w-4 h-4" />
                        <span className="text-sm">Support</span>
                      </div>
                    </div>
                    
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-stone-800">Welcome back, Alex</h3>
                        <Bell className="w-5 h-5 text-stone-400" />
                      </div>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-4 bg-stone-50 rounded-xl">
                          <p className="text-xs text-stone-500">Revenue</p>
                          <p className="text-xl font-bold text-stone-900">$12,450</p>
                          <p className="text-xs text-green-600">+12.5%</p>
                        </div>
                        <div className="p-4 bg-stone-50 rounded-xl">
                          <p className="text-xs text-stone-500">Clients</p>
                          <p className="text-xl font-bold text-stone-900">248</p>
                          <p className="text-xs text-green-600">+8 new</p>
                        </div>
                        <div className="p-4 bg-stone-50 rounded-xl">
                          <p className="text-xs text-stone-500">Services</p>
                          <p className="text-xl font-bold text-stone-900">32</p>
                          <p className="text-xs text-stone-400">Active</p>
                        </div>
                        <div className="p-4 bg-stone-50 rounded-xl">
                          <p className="text-xs text-stone-500">Renewals</p>
                          <p className="text-xl font-bold text-stone-900">15</p>
                          <p className="text-xs text-amber-600">This week</p>
                        </div>
                      </div>
                      
                      <div className="bg-stone-50 rounded-xl p-4">
                        <div className="space-y-3">
                          {[
                            { text: 'New order from Acme Corp', time: '2 min ago', color: 'bg-green-400' },
                            { text: 'Payment received - $450', time: '1 hour ago', color: 'bg-blue-400' },
                            { text: 'Service renewal: Starter Plan', time: '3 hours ago', color: 'bg-amber-400' }
                          ].map((item, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${item.color}`}></div>
                              <span className="text-sm text-stone-700 flex-1">{item.text}</span>
                              <span className="text-xs text-stone-400">{item.time}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Case Tabs Section */}
      <RevealSection>
      <section className="py-16" data-testid="use-case-tabs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-6">TRUSTED BY AGENCIES WORLDWIDE</p>
            
            {/* Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {USE_CASE_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-stone-900 text-white shadow-lg'
                      : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Active Tab Content */}
            {activeUseCase && (
              <div className="max-w-2xl mx-auto bg-white rounded-2xl p-8 shadow-lg border border-stone-100" data-testid="active-tab-content">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <activeUseCase.icon className="w-8 h-8 text-stone-400" />
                </div>
                <p className="text-lg text-stone-500 mb-2">{activeUseCase.problem}</p>
                <p className="text-2xl font-bold text-stone-900">{activeUseCase.solution}</p>
                <div className="mt-6 flex justify-center">
                  <Link to="/register">
                    <StarBorder
                      as="div"
                      color="#d4a574"
                      speed="4s"
                      thickness={1}
                    >
                      Start free trial
                    </StarBorder>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      </RevealSection>

      {/* Feature Sections */}
      <RevealSection delay={0.1}>
      <div id="features">
        {FEATURES.map((feature, index) => (
          <FeatureSection key={index} feature={feature} index={index} />
        ))}
      </div>
      </RevealSection>

      {/* Comparison CTA */}
      <RevealSection delay={0.1}>
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-stone-100">
            <h3 className="text-2xl font-bold text-stone-900 mb-3">How do we compare?</h3>
            <p className="text-stone-600 mb-6">
              See how Edrano stacks up against SPP.co, SuiteDash, and Copilot
            </p>
            <div className="flex justify-center">
              <Link to="/compare">
                <StarBorder
                  as="div"
                  color="#d4a574"
                  speed="4s"
                  thickness={1}
                >
                  <span className="flex items-center gap-2">
                    View Full Comparison
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </StarBorder>
              </Link>
            </div>
          </div>
        </div>
      </section>
      </RevealSection>

      {/* FAQ Section */}
      <RevealSection>
      <section className="py-20" id="about" data-testid="faq-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold">
              <GradientText
                colors={["#1a1a1a", "#57534e", "#78716c", "#1a1a1a"]}
                animationSpeed={8}
                direction="horizontal"
              >
                Got any questions?
              </GradientText>
            </h2>
            <p className="mt-4 text-stone-600">
              Everything you need to know about Edrano before you start.
            </p>
          </div>
          
          <div className="mt-12">
            {FAQ_ITEMS.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>
      </RevealSection>

      {/* Pricing Section */}
      <RevealSection>
      <section className="py-20" id="pricing" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold">
              <GradientText
                colors={["#1a1a1a", "#57534e", "#78716c", "#1a1a1a"]}
                animationSpeed={8}
                direction="horizontal"
              >
                Simple, transparent pricing
              </GradientText>
            </h2>
            <p className="mt-4 text-stone-600">
              No hidden fees. No transaction charges. Start free, upgrade anytime.
            </p>

            <div className="flex items-center justify-center gap-4 mt-8">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-stone-900' : 'text-stone-400'}`}>
                Monthly
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  isAnnual ? 'bg-stone-900' : 'bg-stone-300'
                }`}
                data-testid="billing-toggle"
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  isAnnual ? 'left-7' : 'left-1'
                }`} />
              </button>
              <span className={`text-sm font-medium ${isAnnual ? 'text-stone-900' : 'text-stone-400'}`}>
                Annual <span className="text-green-600">(Save 17%)</span>
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {PRICING_TIERS.map((tier, index) => (
              <div 
                key={index}
                className={`relative p-8 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl cursor-pointer ${
                  tier.popular 
                    ? 'bg-white border-2 border-stone-900 shadow-xl' 
                    : 'bg-white border border-stone-200 hover:border-stone-300'
                }`}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-stone-900 text-white text-xs font-medium rounded-full">
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-stone-900">{tier.name}</h3>
                  <p className="text-sm text-stone-500 mt-1">{tier.description}</p>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-stone-900">
                    ${isAnnual ? Math.round(tier.annualPrice / 12) : tier.monthlyPrice}
                  </span>
                  <span className="text-stone-500">/month</span>
                  {isAnnual && (
                    <p className="text-sm text-stone-400 mt-1">
                      ${tier.annualPrice} billed annually
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-stone-400 flex-shrink-0" />
                      <span className="text-sm text-stone-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex justify-center">
                  <Link to="/register">
                    <StarBorder
                      as="div"
                      color={tier.popular ? "#d4a574" : "#a89070"}
                      speed="4s"
                      thickness={1}
                      className={tier.popular ? "" : "light"}
                      data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                    >
                      {tier.cta}
                    </StarBorder>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center mt-8 text-sm text-stone-500">
            <span className="text-green-600 font-medium">0% transaction fees</span> on all direct plans. Keep 100% of what you earn.
          </p>
        </div>
      </section>
      </RevealSection>

      {/* Final CTA Section */}
      <RevealSection>
      <section className="py-20" data-testid="cta-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="rounded-3xl overflow-hidden relative"
            style={{ 
              height: '600px',
              background: '#0a0a0a'
            }}
          >
            {/* Tetrahedron Monolith Background */}
            <div className="absolute inset-0">
              <TetrahedronMonolith className="absolute inset-0" />
            </div>
            
            {/* Content Overlay */}
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Edrano gives SMBs the stable framework they need to craft, sell, and scale their services.
              </h2>
              <p className="text-white/70 mb-10 max-w-2xl mx-auto text-lg">
                Launch your services on Edrano today.
              </p>
              <Link to="/register">
                <StarBorder
                  as="div"
                  color="#c4b5a0"
                  speed="4s"
                  thickness={1}
                  data-testid="cta-final"
                >
                  <span className="flex items-center gap-2">
                    Start your workflow
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </StarBorder>
              </Link>
            </div>
          </div>
        </div>
      </section>
      </RevealSection>

      {/* Footer */}
      <footer className="py-12 border-t border-stone-200" data-testid="marketing-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
                <span className="font-semibold text-stone-900">{SITE_CONFIG.name}</span>
              </div>
              <p className="text-xs text-stone-400 mt-4">
                © {new Date().getFullYear()} {SITE_CONFIG.name}. All rights reserved.
                <br />
                <span className="mt-1 inline-block">
                  Part of{' '}
                  <a 
                    href="https://themelio.app" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-orange-500 hover:underline"
                  >
                    Themelio
                  </a>
                </span>
              </p>
            </div>

            <div className="flex gap-16">
              <div>
                <h4 className="font-medium text-stone-900 mb-4">Pages</h4>
                <ul className="space-y-2">
                  {FOOTER_LINKS.main.map((link, i) => (
                    <li key={i}>
                      {link.href.startsWith('/') ? (
                        <Link to={link.href} className="text-sm text-stone-500 hover:text-stone-900 transition-colors">
                          {link.name}
                        </Link>
                      ) : (
                        <a href={link.href} className="text-sm text-stone-500 hover:text-stone-900 transition-colors">
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-stone-900 mb-4">More pages</h4>
                <ul className="space-y-2">
                  {FOOTER_LINKS.more.map((link, i) => (
                    <li key={i}>
                      <Link to={link.href} className="text-sm text-stone-500 hover:text-stone-900 transition-colors">
                        {link.name}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button
                      onClick={() => {
                        localStorage.removeItem('edrano_cookie_consent');
                        window.dispatchEvent(new Event('cookie-consent-reset'));
                      }}
                      className="text-sm text-stone-500 hover:text-stone-900 transition-colors"
                      data-testid="cookie-settings-link"
                    >
                      Cookie Settings
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
